/**
 * Who was asked each question, from the server's base check (`/base-check`).
 *
 * A server built without the base check has no such endpoint, so "no routing
 * information" is an ordinary result here, never an error: the tables then
 * use the "answered" base. The Conductor can also refuse a request with HTTP
 * 200 and a body of `{"Err msg": "Error <code>: ..."}`; that body is not a
 * report and reads as "unavailable" too.
 */
import { AxiosInstance, AxiosRequestConfig, isAxiosError } from "axios"

import { readRouting, RoutingResult, UNAVAILABLE } from "../model/routing"

export { readRouting }
export type { RoutingResult }

const POLL_MS = 3000
const MAX_WAIT_MS = 3 * 60 * 1000
/** How long one request may take before the check is given up. */
const REQUEST_TIMEOUT_MS = 30 * 1000

const TOO_LONG = "The routing check took too long and was given up."
/** A request that did not answer in time: the server is as good as without the check. */
const TIMED_OUT: RoutingResult = { ok: false, reason: "unavailable", message: TOO_LONG }
/** The whole check, requests included, ran past its longest wait. */
const GAVE_UP: RoutingResult = { ok: false, reason: "failed", message: TOO_LONG }

/** A request that has not answered within `REQUEST_TIMEOUT_MS`. */
class RequestTimeout extends Error {}

/**
 * The request, or `RequestTimeout` once its time is up. Axios's own
 * `timeout` stops the request itself; this also ends the wait for a request
 * whose adapter does not honour it.
 */
const inTime = <T>(request: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new RequestTimeout()), ms)
    request.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })

const isTimeout = (e: unknown): boolean =>
  e instanceof RequestTimeout ||
  (isAxiosError(e) && (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT"))

/**
 * Asks the server for the routing of every question. Never throws: a server
 * without the base check, a network failure and a timeout all come back as
 * `{ ok: false }`, and the tables then use the "answered" base. Each request
 * may take 30 seconds (a request that takes longer is "unavailable": the
 * check took too long), and the whole check 3 minutes by the clock, its
 * requests and its waits between polls together ("failed", as a job that
 * never finishes). The waits between polls also count on their own, so a
 * test with a `sleep` that takes no time still ends.
 */
export const loadRouting = async (
  axios: AxiosInstance,
  projectId: string,
  owner: string,
  variant: string | null,
  deps: {
    sleep?: (ms: number) => Promise<void>
    pollIntervalMs?: number
    maxWaitMs?: number
  } = {}
): Promise<RoutingResult> => {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const interval = deps.pollIntervalMs ?? POLL_MS
  const maxWait = deps.maxWaitMs ?? MAX_WAIT_MS
  const deadline = Date.now() + maxWait
  /** GETs `url` in its time: 30 seconds, and never past the end of the whole check. */
  const get = <T = unknown>(url: string, config: AxiosRequestConfig) => {
    const ms = Math.max(1, Math.min(REQUEST_TIMEOUT_MS, deadline - Date.now()))
    return inTime(axios.get<T>(url, { ...config, timeout: ms }), ms)
  }
  // The body is read as text and left as it came: `readRouting` parses it.
  const asText = {
    responseType: "text" as const,
    transformResponse: (data: unknown) => data,
    validateStatus: () => true,
  }
  try {
    let url = `/base-check?id=${encodeURIComponent(projectId)}&hint_owner=${encodeURIComponent(owner)}`
    if (variant) url += `&variant=${encodeURIComponent(variant)}`
    const first = await get<string>(url, { ...asText, headers: { "ASync-Request": 10 } })
    if (first.status === 200) return readRouting(first.data)
    if (first.status !== 202) return UNAVAILABLE

    const job = String(first.data).trim()
    let waited = 0
    while (waited < maxWait) {
      await sleep(interval)
      waited += interval
      if (Date.now() >= deadline) break
      const ready = await get(`/async-ready?id=${encodeURIComponent(job)}`, {
        validateStatus: () => true,
      })
      if (ready.status === 200) {
        const result = await get<string>(`/async-get?id=${encodeURIComponent(job)}`, asText)
        return readRouting(result.data)
      }
      if (ready.status !== 202) return UNAVAILABLE
    }
    return GAVE_UP
  } catch (e) {
    if (!isTimeout(e)) return UNAVAILABLE
    return Date.now() >= deadline ? GAVE_UP : TIMED_OUT
  }
}
